import React, { useEffect, useState } from 'react'
import {
  Box,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Typography,
  Grid2 as Grid,
  CircularProgress,
} from '@mui/material'
import { TextField } from 'components/@extended/TextField'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import SinglePeriodSelector from 'components/SinglePeriodSelector'
import CompensationChart from 'components/CompensationChart'
import { NumericField } from 'components/NumericField'
import Paper from 'components/@extended/Paper'
import { useEditTemplate } from './EditTemplateContext'
import type { Organization } from 'types/organization'
import type { UserRole } from 'types/role'
import type { Period } from 'types/periods'
import { Permissions } from 'types/permissions'
import { ComponentType } from 'types/components'
import { createIdToPeriodFromList } from 'types/periods'
import PercentageRangeInput from 'components/PercentageRangeInput'
import { useTheme } from '@mui/material/styles'
import { ToolOutlined } from '@ant-design/icons'

const TemplateDetail: React.FC = () => {
  const {
    label,
    setLabel,
    position,
    setPosition,
    organizationIds,
    setOrganizationIds,
    setRootOrganizationId,
    periodId,
    setPeriodId,
    setPeriod,
    setRootPeriodId,
    periodProration,
    setPeriodProration,
    compensationModel,
    setCompensationModel,
    variablePercent,
    setVariablePercent,
    minVariablePercent,
    setMinVariablePercent,
    maxVariablePercent,
    setMaxVariablePercent,
    defaultVariableCap,
    setDefaultVariableCap,
    minVariableCap,
    setMinVariableCap,
    maxVariableCap,
    setMaxVariableCap,
    exampleSalary,
    setExampleSalary,
    calculatedBaseComp,
    calculatedVariableComp,
    currency,
    currencyDecimals,
    profile,
    periods,
    isLoading,
  } = useEditTemplate()
  const theme = useTheme()

  const [toggle, setToggle] = useState(true)

  // State for the slider values for variable compensation:width
  const [sliderValuesForVariableComp, setSliderValuesForVariableComp] = useState<number[]>([
    Math.round(minVariablePercent * 100),
    Math.round(variablePercent * 100),
    Math.round(maxVariablePercent * 100),
  ])

  // State for the slider values for maximum variable payout aka maximum variable cap
  const [sliderValuesForVariableCap, setSliderValuesForVariableCap] = useState<number[]>([
    Math.round(minVariableCap * 100),
    Math.round(defaultVariableCap * 100),
    Math.round(maxVariableCap * 100),
  ])

  useEffect(() => {
    setSliderValuesForVariableComp([
      Math.round(minVariablePercent * 100),
      Math.round(variablePercent * 100),
      Math.round(maxVariablePercent * 100),
    ])
  }, [minVariablePercent, variablePercent, maxVariablePercent])

  useEffect(() => {
    setSliderValuesForVariableCap([
      Math.round(minVariableCap * 100),
      Math.round(defaultVariableCap * 100),
      Math.round(maxVariableCap * 100),
    ])
  }, [minVariableCap, defaultVariableCap, maxVariableCap])

  if (isLoading || !profile) {
    return (
      <Paper elevation={2} sx={{ p: 1, pt: 2, minWidth: '1050px', maxWidth: '1100px' }}>
        <Grid container spacing={2} sx={{ mx: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="h5">Information</Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading template details...</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    )
  }

  const getOrganizationMap = (): Record<string, Organization> => {
    return profile!.roles.reduce(
      (acc: Record<string, Organization>, role: UserRole) => {
        if (role.role.permissions.includes(Permissions.PERIODS_READ)) {
          acc[role.organization.id] = role.organization
        }
        return acc
      },
      {} as Record<string, Organization>
    )
  }

  const handlePeriodChange = (event: string) => {
    const selectedId = event
    const selectedPeriod: Period = createIdToPeriodFromList(periods)[selectedId]

    setPeriodId(selectedPeriod.id || '')
    setPeriod(selectedPeriod)
    setRootOrganizationId(selectedPeriod.organizationId || '')
    setRootPeriodId(selectedPeriod.rootPeriodId || '')
    setPeriodProration(selectedPeriod.periodProration || 1.0)
  }

  const handleSliderChangeForVariableComp = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      setMinVariablePercent(newValue[0] / 100)
      setVariablePercent(newValue[1] / 100)
      setMaxVariablePercent(newValue[2] / 100)
      setSliderValuesForVariableComp(newValue)
    }
  }

  const handleSliderChangeForVariableCap = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      setMinVariableCap(newValue[0] / 100)
      setDefaultVariableCap(newValue[1] / 100)
      setMaxVariableCap(newValue[2] / 100)
      setSliderValuesForVariableCap(newValue)
    }
  }

  return (
    <Paper elevation={2} sx={{ p: 3, minWidth: '1050px', maxWidth: '1100px' }}>
      <Grid container spacing={3}>
        {/* Header */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="h5" sx={{ mb: 0 }}>
            Information
          </Typography>
        </Grid>

        {/* Main Content Layout */}
        <Grid size={{ xs: 9 }} container spacing={3}>
          {/* Basic Information Row */}
          <Grid size={{ xs: 6 }}>
            <TextField
              label="Template Name *"
              fullWidth
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Template Name"
              variant="outlined"
              shrink
              pill
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="Position *"
              fullWidth
              type="text"
              placeholder="Position"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              variant="outlined"
              shrink
              pill
            />
          </Grid>

          {/* Organization and Period Row */}
          <Grid size={{ xs: 6 }}>
            <MultiOrganizationSelector
              userProfile={profile}
              selectedOrganizationIds={organizationIds}
              handleChange={(orgIds) => {
                setOrganizationIds(orgIds)
                if (orgIds.length > 0) {
                  const firstOrgRoot = getOrganizationMap()[orgIds[0]]?.rootOrganizationId
                  if (firstOrgRoot) {
                    setRootOrganizationId(firstOrgRoot)
                  }
                }
              }}
              label="Select Teams *"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SinglePeriodSelector
              key="period-selector"
              periods={periods}
              value={periodId}
              label="Select Period *"
              onChange={handlePeriodChange}
              rootPeriodOnly={false}
              sortOrder="asc"
              variant="outlined"
              shrink
              pill
            />
          </Grid>

          {/* Compensation Settings Row */}
          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="comp-model-select-label">Compensation Model *</InputLabel>
              <Select
                value={compensationModel}
                onChange={(e) => {
                  setCompensationModel(e.target.value as string)
                  if (e.target.value === 'BONUS_ONLY') {
                    setVariablePercent(1)
                    setMinVariablePercent(1)
                    setMaxVariablePercent(1)
                  } else {
                    setVariablePercent(0.1)
                    setMinVariablePercent(0.05)
                    setMaxVariablePercent(0.15)
                  }
                }}
                variant="outlined"
                style={{ borderRadius: '21px' }}
                label="Compensation Model *"
              >
                <MenuItem value="BASE_PLUS">Base Plus</MenuItem>
                <MenuItem value="BONUS_ONLY">Bonus Only</MenuItem>
                <MenuItem value="BONUS_INCLUDED">Bonus Included</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <NumericField
              componentFormat={ComponentType.COMPONENT_TYPE_CURRENCY}
              label={
                compensationModel === 'BONUS_ONLY'
                  ? 'Example Annual Bonus *'
                  : compensationModel === 'BONUS_INCLUDED'
                    ? 'Example On-Target-Earnings *'
                    : 'Example Annual Base Salary *'
              }
              value={exampleSalary}
              onValueChange={(value: any) => {
                const numericValue = Number(value.value)
                if (!isNaN(numericValue)) {
                  setExampleSalary(numericValue)
                }
              }}
              variant="outlined"
              pill
              shrink
            />
          </Grid>
          {/* Variable Compensation Controls Row */}
          <Grid size={{ xs: 6 }} sx={{ minHeight: '120px' }}>
            {toggle ? (
              <Box
                sx={{ px: 2, minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '10px' }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: '0.75rem',
                      lineHeight: '1rem',
                    }}
                  >
                    {compensationModel === 'BONUS_ONLY' ? 'Bonus Compensation *' : 'Variable Compensation *'}
                  </Typography>
                  <IconButton
                    onClick={() => setToggle(!toggle)}
                    size="small"
                    color="primary"
                    sx={{
                      border: 0,
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      padding: '0px',
                    }}
                  >
                    <ToolOutlined style={{ fontSize: 12 }} />
                  </IconButton>
                </Box>
                <Slider
                  key="slider-variable-compensation"
                  value={sliderValuesForVariableComp}
                  onChange={handleSliderChangeForVariableComp}
                  min={0}
                  max={50}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 50, label: '50%' },
                    { value: sliderValuesForVariableComp[0], label: `${sliderValuesForVariableComp[0]}%` },
                    { value: sliderValuesForVariableComp[2], label: `${sliderValuesForVariableComp[2]}%` },
                    { value: sliderValuesForVariableComp[1], label: `${sliderValuesForVariableComp[1]}%` },
                  ]}
                  step={1}
                  disabled={compensationModel === 'BONUS_ONLY'}
                  sx={{ width: '100%' }}
                />
              </Box>
            ) : (
              <Box sx={{ minHeight: '120px', display: 'flex', alignItems: 'center' }}>
                <PercentageRangeInput
                  label="Variable Compensation *"
                  defaultValue={variablePercent}
                  setDefaultValue={setVariablePercent}
                  minValue={minVariablePercent}
                  setMinValue={setMinVariablePercent}
                  maxValue={maxVariablePercent}
                  setMaxValue={setMaxVariablePercent}
                  disabled={compensationModel === 'BONUS_ONLY'}
                  toggleAction={
                    <IconButton
                      onClick={() => setToggle(true)}
                      size="small"
                      color="primary"
                      sx={{
                        border: 0,
                        borderColor: 'primary.main',
                        borderRadius: 1,
                        padding: '0px',
                      }}
                    >
                      <ToolOutlined style={{ fontSize: 12 }} />
                    </IconButton>
                  }
                />
              </Box>
            )}
          </Grid>
          <Grid size={{ xs: 6 }} sx={{ minHeight: '120px' }}>
            {toggle ? (
              <Box
                sx={{ px: 2, minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
              >
                <Typography
                  gutterBottom
                  variant="body1"
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: '0.75rem',
                    lineHeight: '1rem',
                    marginBottom: '10px',
                  }}
                >
                  Maximum Variable Payout *
                </Typography>
                <Slider
                  key="slider-variable-cap"
                  value={sliderValuesForVariableCap}
                  onChange={handleSliderChangeForVariableCap}
                  min={100}
                  max={300}
                  marks={[
                    { value: 100, label: '100%' },
                    { value: 300, label: '300%' },
                    { value: sliderValuesForVariableCap[0], label: `${sliderValuesForVariableCap[0]}%` },
                    { value: sliderValuesForVariableCap[2], label: `${sliderValuesForVariableCap[2]}%` },
                    { value: sliderValuesForVariableCap[1], label: `${sliderValuesForVariableCap[1]}%` },
                  ]}
                  step={1}
                  sx={{ width: '100%' }}
                  size="small"
                />
              </Box>
            ) : (
              <Box sx={{ minHeight: '120px', display: 'flex', alignItems: 'center' }}>
                <PercentageRangeInput
                  label="Maximum Variable Payout *"
                  defaultValue={defaultVariableCap}
                  setDefaultValue={setDefaultVariableCap}
                  minValue={minVariableCap}
                  setMinValue={setMinVariableCap}
                  maxValue={maxVariableCap}
                  setMaxValue={setMaxVariableCap}
                />
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Compensation Chart Section */}
        <Grid size={{ xs: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: '100%',
              pl: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              {periodProration === 1 ? 'Annual Summary' : 'Prorated Summary'}
            </Typography>

            <CompensationChart
              key="compensation-chart"
              salary={calculatedBaseComp}
              variable={calculatedVariableComp}
              currency={currency}
              currencyDecimals={currencyDecimals}
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  )
}

export default TemplateDetail
